/**
 * Social Sharing Type System & URL Helpers
 *
 * HOW TO ADD SHARING TO A NEW METRIC:
 * 1. Add entry to ShareCardType enum
 * 2. Define its data type (e.g. MetabolicRateShareData) and add to ShareCardData union
 * 3. Add encode/decode cases in encodeShareData/decodeShareData
 * 4. Create cards/XxxShareCard.tsx (9:16, all inline styles, forwardRef)
 * 5. Add case to card registry in SharePage.tsx
 * 6. In your widget: const { openShare, ShareModalComponent } = useShare()
 *    then call openShare(data) from any button/trigger
 */

export enum ShareCardType {
  BIO_AGE = "bio_age",
  // Future: METABOLIC_RATE = "metabolic_rate", VO2_MAX = "vo2_max"
}

export type BioAgeShareData = {
  type: ShareCardType.BIO_AGE;
  bioAge: number;
  chronologicalAge: number;
  customShare?: string | null;
};

export type ShareCardData = BioAgeShareData;

export function encodeShareData(data: ShareCardData): URLSearchParams {
  const params = new URLSearchParams();
  switch (data.type) {
    case ShareCardType.BIO_AGE:
      params.set("t", data.type);
      params.set("ba", String(data.bioAge));
      params.set("ca", String(data.chronologicalAge));
      if (data.customShare) {
        params.set("cs", data.customShare);
      }
      break;
  }
  return params;
}

export function decodeShareData(params: URLSearchParams): ShareCardData | null {
  const type = params.get("t");

  switch (type) {
    case ShareCardType.BIO_AGE: {
      // Support new "ba" param, fall back to legacy "bam" for backward compat
      const ba = params.get("ba") ?? params.get("bam");
      const ca = params.get("ca");
      if (!ba || !ca) return null;
      const bioAge = Number(ba);
      const chronologicalAge = Number(ca);
      if (isNaN(bioAge) || isNaN(chronologicalAge)) return null;
      const customShare = params.get("cs") || null;
      return {
        type: ShareCardType.BIO_AGE,
        bioAge,
        chronologicalAge,
        customShare,
      };
    }
    default:
      return null;
  }
}

export function getShareBaseUrl(): string {
  const hostname = window.location.hostname;
  switch (hostname) {
    case "welcome.mypnoe.com":
    case "welcome-staging.mypnoe.com":
      return "https://welcome.mypnoe.com";
    case "welcome-dev.mypnoe.com":
      return "https://welcome-dev.mypnoe.com";
    default: {
      // For local dev: replace localhost with the machine's LAN IP
      // so the QR code is scannable from a phone on the same network.
      if (hostname === "localhost" || hostname === "127.0.0.1") {
        const lanIp = process.env.REACT_APP_LAN_IP;
        if (lanIp) {
          const protocol = window.location.protocol;
          const port = window.location.port;
          return `${protocol}//${lanIp}${port ? `:${port}` : ""}`;
        }
      }
      return window.location.origin;
    }
  }
}

export function buildShareUrl(data: ShareCardData): string {
  const base = getShareBaseUrl();
  const params = encodeShareData(data);
  return `${base}/share?${params.toString()}`;
}
